import React, { useEffect } from 'react'
import { Form, schema } from '@ggs/forms'
import { useI18n } from '@ggs/i18n'
import { Button, HelpTooltip } from '../../buttons'
import Typography from '@mui/material/Typography'
import TablePagination from '@mui/material/TablePagination'
import { Box, CircularProgress } from '@mui/material'
import { CourseSectionsList, SelectOptionControl } from '../../'
import { useCourseSearch } from '../../../local'
import { size, isEmpty } from 'lodash'
import { getViewedPageParams } from '@ggs/utils'
import './CourseSearchForm.scss'

// Select dropdown field options.
import rowsPerPageOptions from './rowPerPageOptions.json'
import instructionalFormatOptions from './instructionalFormatOptions.json'
import deliveryModeOptions from './deliveryModeOptions.json'
import personAttendanceRequiredOptions from './personAttendanceRequiredOptions.json'

/**
 * @typedef {Object} CourseSearchFormProps
 */

/**
 * Course search form.
 * @return {JSX.Element}
 */
export function CourseSearchForm() {
  const { t } = useI18n()
  const queryStringValues = getViewedPageParams()
  const defaultFilters = {}

  if (queryStringValues?.searchTerm) {
    defaultFilters.title = queryStringValues.searchTerm
  }

  const { handlers, state } = useCourseSearch({
    defaultFilters,
  })

  const {
    currentFilters,
    currentPage,
    currentPageIndex,
    loading,
    pageResults,
    pagesIndexOptions,
    resultsCount,
    rowsPerPage,
  } = state

  const fieldsDefinition = {
    courseSubject: {
      label: t('Course Subject'),
      type: schema.Forms.FieldTypes.text,
      variant: 'standard',
      afterFieldText: <HelpTooltip message={t('Search the subject of all UBC Courses (Example: BIOL_O or BIOL_V).')} />,
    },
    courseNumber: {
      label: t('Course Number'),
      type: schema.Forms.FieldTypes.text,
      afterFieldText: <HelpTooltip message={t('Search by course number. E.g 100')} />,
      variant: 'standard',
      // helpText: t('API unsupported filter'),
    },
    title: {
      label: t('Keyword(s)'),
      type: schema.Forms.FieldTypes.text,
      afterFieldText: (
        <HelpTooltip
          message={t(
            'Search on course title, not case sensitive. Use the wildcard "%" in the pattern to ' +
              'match against zero of more occurrences of any character. Use the wildcard "_" in the ' +
              'pattern to match against a single occurrence of any character.'
          )}
        />
      ),
      variant: 'standard',
      // helpText: t('API unsupported filter'),
    },
    instructionalFormat: {
      label: t('Instructional Method'),
      type: schema.Forms.FieldTypes.select,
      afterFieldText: (
        <HelpTooltip
          message={t('Select the type of course to search for. (e.g. Lecture, Tutorial, etc.')}
        />
      ),
      variant: 'standard',
      // helpText: t('API unsupported filter'),
      options: instructionalFormatOptions,
    },
    deliveryMode: {
      label: t('Delivery Mode'),
      type: schema.Forms.FieldTypes.select,
      afterFieldText: (
        <HelpTooltip
          message={
            <div>
              {t('Search for a course with a specific delivery mode:')}
              <ul>
                <li>{t('In-Person – The course activity is taking place in-person.')}</li>
                <li>
                  {t(
                    'Online – The course activity is fully online and does not require any in-person attendance to complete.'
                  )}
                </li>
                <li>
                  {t(
                    'Hybrid – The course activity has a mixture of mandatory in-person and online activities as designed by the instructor.'
                  )}
                </li>
                <li>
                  {t(
                    'Multi-Access - The course activity allows students the choice to complete in-person or online.'
                  )}
                </li>
              </ul>
            </div>
          }
        />
      ),
      // helpText: t('API unsupported filter'),
      variant: 'standard',
      options: deliveryModeOptions,
    },
    creditUnits: {
      label: t('Credits'),
      type: schema.Forms.FieldTypes.text,
      afterFieldText: (
        <HelpTooltip message={t('Specify the number of course credits to search for')} />
      ),
      variant: 'standard',
      // helpText: t('API unsupported filter'),
    },
  }

  const FormInstance = Form.useForm({
    withDraftState: false,
    formId: 'form_course__search',
    className: 'form_course__search',
    // @ts-ignore
    fields: fieldsDefinition,
    actions: [
      {
        type: 'submit',
        label: t('Search for Courses'),
        variant: Button.variant.contained,
      },
    ],
    sx: {},
    onSubmit: handlers.onSubmitHandler,
  })

  /**
   * Handle update form select field options.
   */
  const updateSelectFieldOptions = () => {
    const newFields = {
      ...FormInstance.fields,
      title: {
        ...FormInstance.fields['title'],
        value: currentFilters.title || '',
      },
    }

    FormInstance.setFields(newFields)
  }

  useEffect(() => {
    updateSelectFieldOptions()
  }, [])

  const startResultIndex = currentPage * rowsPerPage + 1
  const endResultIndex = Math.min(startResultIndex + rowsPerPage - 1, resultsCount)

  return (
    <Box className="course-search">
      <Box
        sx={{
          mt: 2,
        }}
      >
        {FormInstance.FormComp}
        {loading && (
          <Box
            display={'flex'}
            justifyContent={'start'}
            sx={{
              mt: 2,
            }}
          >
            <>
              <CircularProgress size={24} />
              <Typography sx={{ ml: 2 }}>{t('Loading...')}</Typography>
            </>
          </Box>
        )}
        {!loading && !isEmpty(currentFilters) && resultsCount === 0 && (
          <Box className="course-search__results">
            <p className="course-search__empty-notice">
              {t('No results found matching the search criteria.')}
            </p>
          </Box>
        )}
        {!loading && resultsCount > 0 && (
          <Box className="course-search__results">
            <Box className="course-search__results-header">
              <h4 className="course-search__results-title">
                {t('Search Results: {{resultsCount}} results found', {
                  resultsCount: resultsCount,
                })}
              </h4>
              <Box className="course-search__results-header-filter">
                <SelectOptionControl
                  id="page-items"
                  options={rowsPerPageOptions}
                  defaultValue={`${rowsPerPage}`}
                  selectLabel={t('Rows per Page:')}
                  onSelectOption={handlers.handleRowsPerPage}
                />
              </Box>
            </Box>
            <Box className="course-search__results-table">
              <CourseSectionsList
                courseSections={pageResults}
                sectionViewBasePath="/browse-courses/course-section"
              />
            </Box>
            <Box className="course-search__results-footer">
              <Box className="course-search__results-footer-pagination">
                <TablePagination
                  component="div"
                  count={resultsCount}
                  page={currentPage}
                  onPageChange={handlers.handleChangePage}
                  rowsPerPage={Number(rowsPerPage)}
                  rowsPerPageOptions={[]}
                />
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  )
}
